import { FunctionMenuEnum, LottoConfigKey } from './constants'
import { BetType } from './types'
import {
  generateNineteenNumber,
  generatePermutations,
  generateSwitchNumber,
} from './utils/useSelectBetNumber.utils'
import { hasTwoDigitSpecial } from './utils/useSelectBetType.utils'
import useBetControl from '../useBetControl'
import { BetTypeEnum } from '@/ui-components/components/BetLotto/BetLotto.types'
import { useLottoConfig } from './useLottoConfig'

export const useSelectBetNumber = (
  addLottoListAndHandleResponse: ReturnType<
    typeof useBetControl
  >['addLottoListAndHandleResponse'],
) => {
  const { lottoConfig } = useLottoConfig()

  const betTwoKeys = Object.keys(lottoConfig.betTwo)
  const betThreeKeys = Object.keys(lottoConfig.betThree)

  const isConfigKeyValid = (
    configKey: string,
  ): configKey is keyof typeof lottoConfig => {
    return configKey in lottoConfig
  }

  const isMenuOptionValid = (
    configKey: keyof typeof lottoConfig,
    menuOption: BetType,
  ): boolean => {
    return Boolean(lottoConfig[configKey][menuOption])
  }

  const addLottoListIfConfigExists = async (
    currentNumber: string[],
    selectedBetTypes: BetType[],
    configKey: LottoConfigKey,
    digitCount: number,
    menuOption: BetType,
  ): Promise<void> => {
    if (
      isConfigKeyValid(configKey) &&
      isMenuOptionValid(configKey, menuOption)
    ) {
      const currentBet: string[] = [...currentNumber.map(String)].slice(
        0,
        digitCount,
      )
      const setSelect = new Set([...selectedBetTypes])
      const isSpecialTwoDigit = hasTwoDigitSpecial(setSelect)
      if (!(configKey === LottoConfigKey.BET_TWO && isSpecialTwoDigit)) {
        // TODO: find way to remove `as BetTypeEnum[]`
        addLottoListAndHandleResponse(currentBet.join(''), [
          menuOption,
        ] as BetTypeEnum[])
      }
    }
  }

  const handleNumbers = (
    numbers: string[],
    selectedBetTypes: BetType[],
    betKeys: string[],
  ) => {
    if (selectedBetTypes.some((i) => betKeys.includes(i))) {
      for (const number of numbers) {
        addLottoListAndHandleResponse(number, selectedBetTypes as BetTypeEnum[])
      }
    }
  }

  const handleSelectBetNumber = (
    number: string,
    selectedBetTypes: BetType[],
  ) => {
    const currentNumber: string[] = [...number]
    selectedBetTypes.forEach((menuOption) => {
      addLottoListIfConfigExists(
        currentNumber,
        selectedBetTypes,
        LottoConfigKey.BET_SIX,
        6,
        menuOption,
      )
      addLottoListIfConfigExists(
        currentNumber,
        selectedBetTypes,
        LottoConfigKey.BET_FOUR,
        4,
        menuOption,
      )
      addLottoListIfConfigExists(
        currentNumber,
        selectedBetTypes,
        LottoConfigKey.BET_THREE,
        3,
        menuOption,
      )
      addLottoListIfConfigExists(
        currentNumber,
        selectedBetTypes,
        LottoConfigKey.BET_TWO,
        2,
        menuOption,
      )
      addLottoListIfConfigExists(
        currentNumber,
        selectedBetTypes,
        LottoConfigKey.BET_ONE,
        1,
        menuOption,
      )

      switch (menuOption) {
        case FunctionMenuEnum.START_NUMBER: {
          const sNumber = generateSwitchNumber(currentNumber?.[0])
          handleNumbers(sNumber, selectedBetTypes, betTwoKeys)
          break
        }
        case FunctionMenuEnum.END_NUMBER: {
          const eNumber = generateSwitchNumber(currentNumber?.[0], true)
          handleNumbers(eNumber, selectedBetTypes, betTwoKeys)
          break
        }
        case FunctionMenuEnum.NINETEEN_NUMBER: {
          const nNumber = generateNineteenNumber(currentNumber?.[0])
          handleNumbers(nNumber, selectedBetTypes, betTwoKeys)
          break
        }
        case FunctionMenuEnum.SWAP_TWO: {
          const pCurrentBet = [...currentNumber].slice(0, 2)
          const swapTwoNumber = generatePermutations(pCurrentBet)
          const filteredBetTypes = selectedBetTypes.filter((i) =>
            betTwoKeys.includes(i),
          )
          handleNumbers(swapTwoNumber, filteredBetTypes, betTwoKeys)
          break
        }
        case FunctionMenuEnum.SWAP_THREE: {
          const pSwapCurrentBet = [...currentNumber].slice(0, 4)
          const swapThreeNumbers = generatePermutations(pSwapCurrentBet)
          const filteredBetTypes = selectedBetTypes.filter((i) =>
            betThreeKeys.includes(i),
          )
          handleNumbers(swapThreeNumbers, filteredBetTypes, betThreeKeys)
          break
        }
      }
    })
  }

  return {
    handleSelectBetNumber,
  }
}
